import { AHText, Arrow, Box, TouchableBox, useLayoutContext } from "arkh";
import { RouteProp } from "@react-navigation/core";
import React, { useCallback, useMemo } from "react";
import { SectionList, SectionListRenderItem } from "react-native";

import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { UserLoggedInNavigatorParamList } from "../../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { graphql, useLazyLoadQuery } from "react-relay";
import { WidgetTaskCollectionScreenQuery } from "./__generated__/WidgetTaskCollectionScreenQuery.graphql";
import { TaskExecution } from "../../../../RoutineOccasion/TaskExecution/TaskExecutionListItem";
import { BottomSheetModalProvider } from "@gorhom/bottom-sheet";
import { FormattedMessage } from "react-intl";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import { TaskExecutionFooterTodos } from "../../../../RoutineOccasion/TaskExecution/TaskExecutionFooter";
import {
  TodoBottomSheetProvider,
  useTodoBottomSheet,
} from "../../../../ReportIssueBottomSheet/TodoBottomSheet/TodoBottomSheetProvider";
import { withWebModalStack } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModalStack";
import { useNavigationHeader } from "../../../../../common/navigationHeaderOptions";
type Nav = NativeStackNavigationProp<
  UserLoggedInNavigatorParamList,
  "WidgetTaskCollection"
>;

const WIDGET_TASK_COLLECTION_SCREEN_QUERY = graphql`
  query WidgetTaskCollectionScreenQuery($widgetId: ID!) {
    widgetTaskCollection(id: $widgetId) @required(action: THROW) {
      id
      title
      taskSections {
        id
        section {
          id
          title
        }
        task {
          id
          title
          routine {
            id
            title
          }
        }
        inProgressTaskExecution {
          id
          ...TaskExecutionListItemComponent_taskExecution
          ...TaskExecutionFooterTodosComponent_TaskExecution
          routineOccasion {
            id
            sequenceNumber
          }
        }
      }
    }
  }
`;

const WidgetTaskCollection = (props: {
  widgetId: string;
  navigation: Nav;
  fetchKey: string;
}) => {
  const data = useLazyLoadQuery<WidgetTaskCollectionScreenQuery>(
    WIDGET_TASK_COLLECTION_SCREEN_QUERY,
    {
      widgetId: props.widgetId,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const widget = data.widgetTaskCollection;

  type TaskExecution =
    (typeof widget.taskSections)[number]["inProgressTaskExecution"];

  const reportIssueBottomSheet = useTodoBottomSheet();
  const renderResult = useCallback(
    (
      taskExecution: TaskExecution,

      taskId: string
    ) => {
      if (!taskExecution) {
        return null;
      }

      return (
        <>
          <TaskExecution
            taskExecution={taskExecution}
            openInstructions={() => {
              props.navigation.navigateDeprecated(
                "TaskExecutionInstructionsStack",
                {
                  screen: "TaskExecutionInstructions",
                  params: {
                    taskId,
                  },
                }
              );
            }}
            createIssue={reportIssueBottomSheet.createIssue}
          />
          <TaskExecutionFooterTodos taskExecution={taskExecution} />
        </>
      );
    },
    [props.navigation, reportIssueBottomSheet.createIssue]
  );

  type TaskSection = (typeof widget.taskSections)[number];

  const renderRow: SectionListRenderItem<TaskSection> = useCallback(
    ({ index, item, section }) => (
      <>{renderResult(item.inProgressTaskExecution, item.task.id)}</>
    ),

    [renderResult]
  );

  const groupedTasks: {
    [key: string]: [TaskSection];
  } = useMemo(() => {
    return widget.taskSections.reduce(
      (acc, curr) => ({
        ...acc,
        [curr.task.routine.id]: [...(acc[curr.task.routine.id] ?? []), curr],
      }),
      {} as any
    );
  }, [widget.taskSections]);

  const sections = useMemo(
    () =>
      Object.keys(groupedTasks).map((routineId) => ({
        data: groupedTasks[routineId],
        key: routineId,
      })),
    [groupedTasks]
  );
  const insets = useSafeAreaInsets();
  const layout = useLayoutContext();
  return (
    <SectionList
      sections={sections}
      renderItem={renderRow}
      contentContainerStyle={{
        paddingHorizontal: layout.space.medium,
        paddingTop: layout.space.medium,
        paddingBottom: insets.bottom + layout.space.medium,
      }}
      keyExtractor={(item) => item.id}
      stickySectionHeadersEnabled={false}
      renderSectionHeader={({ section }) => {
        const t = section.data.find(
          (t) => t.inProgressTaskExecution?.routineOccasion
        );
        if (sections.length === 1 || !t) {
          return null;
        }
        return (
          <TouchableBox
            style={{
              flexDirection: "row",
              flex: 1,
              justifyContent: "space-between",
              alignItems: "center",
            }}
            space="medium"
            spaceBottom="small"
            onPress={() => {
              if (!t.inProgressTaskExecution) {
                throw new Error(
                  "WidgetTaskCollectionScreen No in progress task execution"
                );
              }
              props.navigation.replace("RoutineOccasion", {
                routineId: section.data[0].task.routine.id,
                sequenceNumber:
                  t.inProgressTaskExecution.routineOccasion.sequenceNumber,
                isNotFeedOrigin: true,
              });
            }}
          >
            <Box>
              <AHText type="title">{section.data[0].task.routine.title}</AHText>
              <AHText type="subtitle" color="primary">
                <FormattedMessage
                  defaultMessage="Show full Checklist"
                  description="WidgetTaskCollectionScreen show full Checklist button"
                />
              </AHText>
            </Box>
            <Arrow color="primary" />
          </TouchableBox>
        );
      }}
    />
  );
};

type WidgetTaskCollectionScreenProps = {
  route: RouteProp<UserLoggedInNavigatorParamList, "WidgetTaskCollection">;
  navigation: Nav;
};

export const WidgetTaskCollectionScreen = withWebModalStack(
  (props: WidgetTaskCollectionScreenProps) => {
    useNavigationHeader({
      type: "CLOSE",
      title: "",
      onClose: props.navigation.goBack,
    });
    const { setOptions } = props.navigation;
    const onBottomSheetClosed = useCallback(
      () => setOptions({ gestureEnabled: true }),
      [setOptions]
    );
    const onBottomSheetOpen = useCallback(
      () => setOptions({ gestureEnabled: false }),
      [setOptions]
    );

    return (
      <BottomSheetModalProvider>
        <TodoBottomSheetProvider
          openProductDetail={(productId) =>
            props.navigation.navigateDeprecated("Product", {
              screen: "ProductMain",
              params: {
                id: productId,
                type: "id",
                state: "details",
              },
            })
          }
          onShowFile={(id) =>
            props.navigation.navigateDeprecated("File", { id })
          }
          onBottomSheetOpen={onBottomSheetOpen}
          onBottomSheetClosed={onBottomSheetClosed}
          openTodoEditCategory={(todoId) => {
            props.navigation.navigateDeprecated("TodoMoveToCategory", {
              todoId,
            });
          }}
        >
          <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
            {(fk) => (
              <WidgetTaskCollection
                fetchKey={fk}
                widgetId={props.route.params.widgetId}
                navigation={props.navigation}
              />
            )}
          </LazyQueryNetworkBoundrary>
        </TodoBottomSheetProvider>
      </BottomSheetModalProvider>
    );
  },
  true
);
